// ============================================================
// 天水數位店長 — 員工名單頁
// 員工 CRUD、在職/離職篩選、新增/編輯彈窗
// 勞基法 §21 基本工資驗證、§38 特休顯示
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';

interface Employee {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  avatar: string | null;
  role: string;
  payType: string;
  hourlyRate: number | null;
  monthlySalary: number | null;
  insuranceGrade: number | null;
  preferredPositions: string[];
  tags: string[];
  employmentType: string;
  hireDate: string;
  terminateDate: string | null;
  isActive: boolean;
  // 個人 PII
  birthday?: string | null;
  idNumber?: string | null;
  address?: string | null;
  bankCode?: string | null;
  bankAccount?: string | null;
  education?: string | null;
  // 備註
  rewardNote?: string | null;
  medicalNote?: string | null;
  otherNote?: string | null;
}

interface EmployeeForm {
  name: string;
  email: string;
  phone: string;
  role: string;
  payType: string;
  hourlyRate: string;
  monthlySalary: string;
  insuranceGrade: string;
  positions: string;
  tags: string;
  employmentType: string;
  hireDate: string;
  // 個人 PII
  birthday: string;
  idNumber: string;
  address: string;
  bankCode: string;
  bankAccount: string;
  education: string;
  // 備註
  rewardNote: string;
  medicalNote: string;
  otherNote: string;
}

const emptyForm: EmployeeForm = {
  name: '', email: '', phone: '', role: 'STAFF', payType: 'HOURLY',
  hourlyRate: '190', monthlySalary: '', insuranceGrade: '', positions: '', tags: '',
  employmentType: 'FULL_TIME', hireDate: '',
  birthday: '', idNumber: '', address: '', bankCode: '', bankAccount: '', education: '',
  rewardNote: '', medicalNote: '', otherNote: '',
};

const roleLabels: Record<string, string> = { OWNER: '老闆', MANAGER: '主管', STAFF: '員工' };
const payTypeLabels: Record<string, string> = { HOURLY: '時薪', MONTHLY: '月薪' };

export function StaffListPage() {
  const { user } = useAuthStore();
  const isManager = user?.role === 'OWNER' || user?.role === 'MANAGER';

  const [staff, setStaff] = useState<Employee[]>([]);
  const [showActive, setShowActive] = useState(true);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EmployeeForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ staff: Employee[] }>(`/staff?active=${showActive}`);
      setStaff(data.staff);
    } catch { /* ignore */ }
    setLoading(false);
  }, [showActive]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  // 本月基本工資（門店設定 → 法規數字），不再寫死在畫面上
  const [minWage, setMinWage] = useState<{ monthly: number; hourly: number } | null>(null);
  useEffect(() => {
    if (!isManager) return;
    api.get<{ current: { minWageMonthly: number; minWageHourly: number } }>('/law-params')
      .then(r => setMinWage({ monthly: r.current.minWageMonthly, hourly: r.current.minWageHourly }))
      .catch(() => { /* 讀不到就不顯示數字，後端仍會檢查 */ });
  }, [isManager]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (emp: Employee) => {
    setEditingId(emp.id);
    setForm({
      name: emp.name,
      email: emp.email,
      phone: emp.phone || '',
      role: emp.role,
      payType: emp.payType,
      hourlyRate: emp.hourlyRate?.toString() || '',
      monthlySalary: emp.monthlySalary?.toString() || '',
      insuranceGrade: emp.insuranceGrade?.toString() || '',
      positions: emp.preferredPositions.join('、'),
      tags: emp.tags.join('、'),
      employmentType: emp.employmentType,
      hireDate: emp.hireDate.slice(0, 10),
      birthday: emp.birthday ? emp.birthday.slice(0, 10) : '',
      idNumber: emp.idNumber ?? '',
      address: emp.address ?? '',
      bankCode: emp.bankCode ?? '',
      bankAccount: emp.bankAccount ?? '',
      education: emp.education ?? '',
      rewardNote: emp.rewardNote ?? '',
      medicalNote: emp.medicalNote ?? '',
      otherNote: emp.otherNote ?? '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone || null,
      role: form.role,
      payType: form.payType,
      hourlyRate: form.payType === 'HOURLY' && form.hourlyRate ? Number(form.hourlyRate) : null,
      monthlySalary: form.payType === 'MONTHLY' && form.monthlySalary ? Number(form.monthlySalary) : null,
      insuranceGrade: form.insuranceGrade ? Number(form.insuranceGrade) : null,
      positions: form.positions ? form.positions.split(/[、,，]/).map(s => s.trim()).filter(Boolean) : [],
      tags: form.tags ? form.tags.split(/[、,，]/).map(s => s.trim()).filter(Boolean) : [],
      employmentType: form.employmentType,
      hireDate: form.hireDate,
      birthday: form.birthday || null,
      idNumber: form.idNumber.trim() || null,
      address: form.address.trim() || null,
      bankCode: form.bankCode.trim() || null,
      bankAccount: form.bankAccount.trim() || null,
      education: form.education.trim() || null,
      rewardNote: form.rewardNote.trim() || null,
      medicalNote: form.medicalNote.trim() || null,
      otherNote: form.otherNote.trim() || null,
    };

    try {
      if (editingId) {
        await api.put(`/staff/${editingId}`, payload);
      } else {
        await api.post('/staff', payload);
      }
      setShowModal(false);
      fetchStaff();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('確定要將此員工設為離職？')) return;
    try {
      await api.put(`/staff/${id}/deactivate`, {});
      fetchStaff();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await api.put(`/staff/${id}/reactivate`, {});
      fetchStaff();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const updateField = (key: keyof EmployeeForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const tenure = (hireDate: string) => {
    const years = (Date.now() - new Date(hireDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    if (years >= 1) return `${Math.floor(years)}年`;
    const months = Math.floor(years * 12);
    return `${months}個月`;
  };

  return (
    <div className="min-h-screen bg-morandi-50 p-4 pb-24">
      {/* Header */}
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-morandi-800 tracking-tight">員工名單</h1>
          <p className="text-sm text-morandi-400 mt-0.5">
            點選員工可進入編輯，{showActive ? '顯示在職人員' : '顯示離職人員'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* 在職/離職 toggle */}
          <button
            onClick={() => setShowActive(!showActive)}
            className={`flex items-center gap-2 px-4 py-2 rounded-btn text-sm font-medium transition-colors min-h-touch
              ${showActive ? 'bg-sage-100 text-sage-600' : 'bg-morandi-200 text-morandi-500'}`}
          >
            <span className={`inline-block w-3 h-3 rounded-full ${showActive ? 'bg-sage-400' : 'bg-morandi-400'}`} />
            {showActive ? '在職' : '離職'}
          </button>
          {isManager && (
            <button onClick={openCreate}
              className="px-4 py-2 bg-smoke-500 text-white rounded-btn font-medium hover:bg-smoke-600 transition-colors min-h-touch">
              + 新增員工
            </button>
          )}
        </div>
      </header>

      {/* Table */}
      <div className="bg-white rounded-card shadow-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-morandi-100">
              <th className="text-left px-4 py-3 text-xs text-morandi-400 font-medium">名稱</th>
              <th className="text-left px-4 py-3 text-xs text-morandi-400 font-medium">行動電話</th>
              <th className="text-left px-4 py-3 text-xs text-morandi-400 font-medium">標籤</th>
              <th className="text-left px-4 py-3 text-xs text-morandi-400 font-medium">工作崗位</th>
              <th className="text-center px-4 py-3 text-xs text-morandi-400 font-medium">正職</th>
              <th className="text-left px-4 py-3 text-xs text-morandi-400 font-medium">薪資</th>
              <th className="text-left px-4 py-3 text-xs text-morandi-400 font-medium">年資</th>
              {isManager && <th className="text-center px-4 py-3 text-xs text-morandi-400 font-medium">操作</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-10 text-morandi-300 text-sm">載入中...</td></tr>
            ) : staff.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-morandi-300 text-sm">
                {showActive ? '目前無在職員工' : '目前無離職員工'}
              </td></tr>
            ) : staff.map((emp) => (
              <tr key={emp.id}
                onClick={() => isManager && openEdit(emp)}
                className={`border-b border-morandi-50 transition-colors ${isManager ? 'cursor-pointer hover:bg-morandi-50' : ''}`}>
                {/* 名稱 + 角色 */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-earth-200 flex items-center justify-center text-earth-600 font-medium flex-shrink-0">
                      {emp.name.slice(0, 1)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-morandi-700">{emp.name}</div>
                      <div className="text-xs text-morandi-300">{roleLabels[emp.role] || emp.role}</div>
                    </div>
                  </div>
                </td>
                {/* 電話 */}
                <td className="px-4 py-3 text-sm text-morandi-600 font-mono">{emp.phone || '—'}</td>
                {/* 標籤 */}
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {emp.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-chip bg-earth-100 text-earth-600">{tag}</span>
                    ))}
                  </div>
                </td>
                {/* 工作崗位 */}
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {emp.preferredPositions.map((pos) => (
                      <span key={pos} className="text-xs px-2 py-0.5 rounded-chip bg-smoke-100 text-smoke-500">{pos}</span>
                    ))}
                  </div>
                </td>
                {/* 正職 */}
                <td className="px-4 py-3 text-center">
                  {emp.employmentType === 'FULL_TIME' ? (
                    <span className="text-sage-500 text-lg">✓</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-chip bg-mist-100 text-mist-500">PT</span>
                  )}
                </td>
                {/* 薪資（老闆權限不顯示薪水） */}
                <td className="px-4 py-3 text-sm text-morandi-600">
                  {emp.role === 'OWNER'
                    ? <span className="text-morandi-300">—</span>
                    : emp.payType === 'HOURLY' ? `$${emp.hourlyRate}/hr` : `$${emp.monthlySalary?.toLocaleString()}/月`}
                </td>
                {/* 年資 */}
                <td className="px-4 py-3 text-sm text-morandi-500">{tenure(emp.hireDate)}</td>
                {/* 操作 */}
                {isManager && (
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {emp.isActive ? (
                      <button onClick={() => handleDeactivate(emp.id)}
                        className="text-xs text-rose-400 hover:text-rose-500 transition-colors px-2 py-1">
                        離職
                      </button>
                    ) : (
                      <button onClick={() => handleReactivate(emp.id)}
                        className="text-xs text-sage-500 hover:text-sage-600 transition-colors px-2 py-1">
                        復職
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 底部統計 */}
      <div className="mt-3 text-xs text-morandi-300 text-right">
        共 {staff.length} 位{showActive ? '在職' : '離職'}員工
      </div>

      {/* Modal — 新增/編輯員工 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-card shadow-card-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-morandi-800 mb-4">
              {editingId ? '編輯員工' : '新增員工'}
            </h2>

            {error && (
              <div className="mb-4 px-4 py-2 bg-rose-50 border border-rose-200 text-rose-600 text-sm rounded-btn">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* 姓名 */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">姓名 *</label>
                  <input value={form.name} onChange={(e) => updateField('name', e.target.value)} required
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                </div>
                {/* Email */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} required
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                </div>
                {/* 電話 */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">行動電話</label>
                  <input value={form.phone} onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="09xxxxxxxx"
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                </div>
                {/* 到職日 */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">到職日 *</label>
                  <input type="date" value={form.hireDate} onChange={(e) => updateField('hireDate', e.target.value)} required
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                </div>
                {/* 角色 */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">角色</label>
                  <select value={form.role} onChange={(e) => updateField('role', e.target.value)}
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700">
                    <option value="STAFF">員工</option>
                    <option value="MANAGER">主管</option>
                    <option value="OWNER">老闆</option>
                  </select>
                </div>
                {/* 正職/兼職 */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">僱用類型</label>
                  <select value={form.employmentType} onChange={(e) => updateField('employmentType', e.target.value)}
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700">
                    <option value="FULL_TIME">正職</option>
                    <option value="PART_TIME">兼職（PT）</option>
                  </select>
                </div>
                {/* 薪資類型 */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">薪資類型</label>
                  <select value={form.payType} onChange={(e) => {
                    updateField('payType', e.target.value);
                    if (e.target.value === 'HOURLY') updateField('monthlySalary', '');
                    else updateField('hourlyRate', '');
                  }}
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700">
                    <option value="HOURLY">時薪</option>
                    <option value="MONTHLY">月薪</option>
                  </select>
                </div>
                {/* 薪資金額 */}
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">
                    {form.payType === 'HOURLY'
                      ? `時薪${minWage ? `（≥$${minWage.hourly}）` : ''}`
                      : `月薪${minWage ? `（≥$${minWage.monthly.toLocaleString()}）` : ''}`}
                  </label>
                  {form.payType === 'HOURLY' ? (
                    <input type="number" value={form.hourlyRate} onChange={(e) => updateField('hourlyRate', e.target.value)}
                      min={minWage?.hourly} placeholder={minWage ? String(minWage.hourly) : ''}
                      className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                  ) : (
                    <input type="number" value={form.monthlySalary} onChange={(e) => updateField('monthlySalary', e.target.value)}
                      min={minWage?.monthly} placeholder={minWage ? String(minWage.monthly) : ''}
                      className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                  )}
                </div>
              </div>

              {/* 勞保投保薪資 */}
              <div>
                <label className="block text-xs text-morandi-400 mb-1">
                  勞保投保薪資（依勞保局核定公文填寫）
                </label>
                <input type="number" value={form.insuranceGrade}
                  onChange={(e) => updateField('insuranceGrade', e.target.value)}
                  min={minWage?.monthly} placeholder={minWage ? `未填則以基本工資 ${minWage.monthly.toLocaleString()} 計算` : '未填則以基本工資計算'}
                  className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                <p className="text-xs text-morandi-400 mt-1">
                  請填「投保薪資金額」，不是級距編號。此金額用來計算勞保／健保自付額。
                </p>
              </div>

              {/* 工作崗位 */}
              <div>
                <label className="block text-xs text-morandi-400 mb-1">工作崗位（用「、」分隔）</label>
                <input value={form.positions} onChange={(e) => updateField('positions', e.target.value)}
                  placeholder="烘焙、發酵、外場、收銀"
                  className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
              </div>
              {/* 標籤 */}
              <div>
                <label className="block text-xs text-morandi-400 mb-1">標籤（用「、」分隔）</label>
                <input value={form.tags} onChange={(e) => updateField('tags', e.target.value)}
                  placeholder="早班、全天、午班"
                  className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
              </div>

              {/* ─── 個人資料 ─── */}
              <div className="border-t border-morandi-200 pt-3">
                <div className="text-sm font-semibold text-morandi-600 mb-2">個人資料</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-morandi-400 mb-1">生日</label>
                    <input type="date" value={form.birthday} onChange={(e) => updateField('birthday', e.target.value)}
                      className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                  </div>
                  <div>
                    <label className="block text-xs text-morandi-400 mb-1">身分證字號</label>
                    <input value={form.idNumber} onChange={(e) => updateField('idNumber', e.target.value.toUpperCase())}
                      maxLength={10} placeholder="A123456789"
                      className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 font-mono" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs text-morandi-400 mb-1">地址</label>
                  <input value={form.address} onChange={(e) => updateField('address', e.target.value)}
                    placeholder="台北市..."
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                </div>
                <div className="mt-3">
                  <label className="block text-xs text-morandi-400 mb-1">學歷</label>
                  <input value={form.education} onChange={(e) => updateField('education', e.target.value)}
                    placeholder="例：xx 高中 / xx 大學食品系"
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700" />
                </div>
              </div>

              {/* ─── 匯款帳號 ─── */}
              <div className="border-t border-morandi-200 pt-3">
                <div className="text-sm font-semibold text-morandi-600 mb-2">匯款資訊</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-morandi-400 mb-1">銀行代碼</label>
                    <input value={form.bankCode} onChange={(e) => updateField('bankCode', e.target.value.replace(/\D/g, ''))}
                      maxLength={3} placeholder="700"
                      className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 font-mono" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-morandi-400 mb-1">匯款帳號</label>
                    <input value={form.bankAccount} onChange={(e) => updateField('bankAccount', e.target.value.replace(/[^\d-]/g, ''))}
                      placeholder="0123456789012"
                      className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 font-mono" />
                  </div>
                </div>
              </div>

              {/* ─── 備註 ─── */}
              <div className="border-t border-morandi-200 pt-3 space-y-3">
                <div className="text-sm font-semibold text-morandi-600">備註</div>
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">獎懲紀錄</label>
                  <textarea value={form.rewardNote} onChange={(e) => updateField('rewardNote', e.target.value)}
                    rows={2} placeholder="例：2026/03 季度全勤獎金、2026/04 遲到口頭警告"
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 resize-none" />
                </div>
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">疾病備註</label>
                  <textarea value={form.medicalNote} onChange={(e) => updateField('medicalNote', e.target.value)}
                    rows={2} placeholder="例：花生過敏、有氣喘 / 緊急聯絡人..."
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 resize-none" />
                </div>
                <div>
                  <label className="block text-xs text-morandi-400 mb-1">其他備註</label>
                  <textarea value={form.otherNote} onChange={(e) => updateField('otherNote', e.target.value)}
                    rows={2} placeholder="自由欄位"
                    className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 resize-none" />
                </div>
              </div>

              {/* 勞基法提示 */}
              <div className="bg-morandi-50 rounded-btn p-3 text-xs text-morandi-400">
                <span className="font-medium text-morandi-500">勞基法提醒：</span>
                {' '}{minWage
                  ? `目前基本工資為月薪 $${minWage.monthly.toLocaleString()}、時薪 $${minWage.hourly}（§21，政府調整時請到「門店設定 → 法規數字」更新）。`
                  : '月薪、時薪不得低於基本工資（§21）。'}
                新進員工滿 6 個月起享有特休假（§38），系統會自動計算。
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 text-morandi-500 hover:text-morandi-700 text-sm transition-colors min-h-touch">
                  取消
                </button>
                <button type="submit" disabled={submitting}
                  className="px-6 py-2.5 bg-smoke-500 text-white rounded-btn font-medium hover:bg-smoke-600 transition-colors min-h-touch">
                  {submitting ? '儲存中...' : editingId ? '儲存變更' : '新增員工'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
